import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdTimeline, MdExpandMore, MdCheck, MdClose, MdCode } from 'react-icons/md';
import { tradingService } from '../../services/tradingService';
import { useAuth } from '../../context/AuthContext';
import PortfolioAnalyticsPanel from './PortfolioAnalyticsPanel';
import ModelPortfolioPanel from './ModelPortfolioPanel';
import StrategyCard from './StrategyCard';
import StrategyDetailModal from './StrategyDetailModal';

// ─── Strategy Code Viewer Modal ──────────────────────────────────────────────
const StrategyCodeModal = ({ strategyKey, onClose, isSuperuser }) => {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState('logic');       // 'logic' | 'vectorized' | 'validation'
  const [copied, setCopied]           = useState(false);

  // Validate / promote state
  const [validating, setValidating]   = useState(false);
  const [validateResult, setValidateResult] = useState(null);   // {checks, all_passed, proposed_key}
  const [promoting, setPromoting]     = useState(false);
  const [promoted, setPromoted]       = useState(false);
  const [promoteKey, setPromoteKey]   = useState('');
  const [promoteError, setPromoteError] = useState('');

  useEffect(() => {
    if (!strategyKey) return;
    setLoading(true);
    setTab('logic');
    setValidateResult(null);
    setPromoted(false);
    setPromoteError('');
    tradingService.getStrategySource(strategyKey)
      .then(r => { setData(r.data); setPromoteKey(r.data?.proposed_key || ''); })
      .catch(() => setData({ error: 'Failed to load source' }))
      .finally(() => setLoading(false));
  }, [strategyKey]);

  // Extract numeric custom id from key like "custom:42"
  const customId = strategyKey?.startsWith('custom:') ? parseInt(strategyKey.split(':')[1], 10) : null;
  const isCustom  = !!customId;
  const canPromote = isSuperuser && isCustom && data?.source === 'custom';

  const activeCode = tab === 'vectorized' ? (data?.vectorized_code || '') : (data?.strategy_code || '');

  const handleCopy = () => {
    navigator.clipboard.writeText(activeCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const handleValidate = async () => {
    if (!customId) return;
    setValidating(true);
    setValidateResult(null);
    setPromoteError('');
    try {
      const r = await tradingService.promoteUserStrategy(customId, { dry_run: true, promoted_key: promoteKey || undefined });
      setValidateResult(r.data);
      if (!promoteKey && r.data.proposed_key) setPromoteKey(r.data.proposed_key);
      setTab('validation');
    } catch (e) {
      setValidateResult({ all_passed: false, checks: [{ name: 'Request failed', passed: false, error: e?.response?.data?.error || 'Network error' }] });
      setTab('validation');
    } finally {
      setValidating(false);
    }
  };

  const handlePromote = async () => {
    if (!customId || !validateResult?.all_passed) return;
    setPromoting(true);
    setPromoteError('');
    try {
      await tradingService.promoteUserStrategy(customId, { dry_run: false, promoted_key: promoteKey || undefined });
      setPromoted(true);
    } catch (e) {
      setPromoteError(e?.response?.data?.error || 'Promotion failed — try again');
    } finally {
      setPromoting(false);
    }
  };

  const tabs = [
    { key: 'logic',      label: 'Layer 1 — Strategy Logic',   sub: 'pandas / backtesting.py' },
    ...(data?.has_vectorized ? [{ key: 'vectorized', label: 'Layer 3 — Vectorized NumPy', sub: 'vectorbt batch optimizer' }] : []),
    ...(validateResult ? [{ key: 'validation', label: 'Validation', sub: '', highlight: validateResult.all_passed ? 'pass' : 'fail' }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-5xl max-h-[90vh] flex flex-col bg-gray-950 border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-3">
            <MdCode size={18} className="text-brand-gold" />
            <div>
              <span className="font-semibold text-gray-100 text-sm">
                {loading ? 'Loading…' : (data?.display_name || strategyKey)}
              </span>
              {data && (
                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  data.is_promoted
                    ? 'bg-green-900/40 text-green-300'
                    : data.source === 'built-in'
                      ? 'bg-blue-900/40 text-blue-300'
                      : data.is_shared
                        ? 'bg-brand-gold/20 text-brand-gold'
                        : 'bg-purple-900/40 text-purple-300'
                }`}>
                  {data.is_promoted ? '⭐ Built-in' : data.source === 'built-in' ? 'Built-in' : data.is_shared ? 'Shared' : 'Custom'}
                </span>
              )}
              {data?.author && (
                <span className="ml-2 text-[10px] text-gray-500">by {data.author}</span>
              )}
              {promoted && (
                <span className="ml-2 text-[10px] text-green-400 font-medium">✓ Promoted!</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
            <MdClose size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800 shrink-0 px-5">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`py-2.5 px-4 text-xs font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-brand-gold text-brand-gold'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              {t.highlight === 'pass' && <span className="mr-1 text-green-400">✓</span>}
              {t.highlight === 'fail' && <span className="mr-1 text-red-400">✗</span>}
              {t.label}
              {t.sub && <span className="ml-1.5 text-[10px] opacity-60">{t.sub}</span>}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden relative">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">Loading source…</div>
          ) : data?.error ? (
            <div className="flex items-center justify-center h-full text-red-400 text-sm">{data.error}</div>
          ) : tab === 'validation' ? (
            /* ── Validation panel ── */
            <div className="h-full overflow-auto p-6">
              {promoted ? (
                <div className="flex flex-col items-center justify-center gap-3 py-8">
                  <div className="w-12 h-12 rounded-full bg-green-900/40 flex items-center justify-center">
                    <MdCheck size={28} className="text-green-400" />
                  </div>
                  <p className="text-green-400 font-semibold">Strategy promoted to built-in!</p>
                  <p className="text-gray-400 text-sm text-center">
                    Registered as <code className="bg-gray-800 px-1.5 py-0.5 rounded text-brand-gold">{promoteKey}</code> in STRATEGY_REGISTRY.<br />
                    Available to all users immediately. Re-registered automatically on every deploy.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Check list */}
                  {validateResult?.checks?.map((c, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                      c.passed
                        ? 'bg-green-950/30 border-green-800/40'
                        : 'bg-red-950/30 border-red-800/40'
                    }`}>
                      <span className={`mt-0.5 shrink-0 text-base ${c.passed ? 'text-green-400' : 'text-red-400'}`}>
                        {c.passed ? '✓' : '✗'}
                      </span>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${c.passed ? 'text-gray-200' : 'text-red-300'}`}>{c.name}</p>
                        {c.detail && <p className="text-[11px] text-gray-500 mt-0.5">{c.detail}</p>}
                        {c.error  && <p className="text-[11px] text-red-400 mt-0.5 font-mono break-all">{c.error}</p>}
                      </div>
                    </div>
                  ))}

                  {/* Summary */}
                  <div className={`mt-4 text-sm font-semibold ${validateResult?.all_passed ? 'text-green-400' : 'text-red-400'}`}>
                    {validateResult?.all_passed ? 'All checks pass.' : 'Fix the errors above before promoting.'}
                  </div>

                  {/* Source info line */}
                  {validateResult?.proposed_key && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      Proposed key: <code className="bg-gray-800 px-1 rounded">{validateResult.proposed_key}</code>
                    </p>
                  )}

                  {/* Promote section — superuser only, after all_passed */}
                  {canPromote && validateResult?.all_passed && !promoted && (
                    <div className="mt-6 pt-4 border-t border-gray-800">
                      <p className="text-xs text-gray-400 mb-3">
                        Promoting registers this strategy as a permanent built-in under a canonical key.<br/>
                        It will be re-registered automatically on every deploy.
                      </p>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <label className="text-[11px] text-gray-500 shrink-0">Registry key</label>
                          <input
                            type="text"
                            value={promoteKey}
                            onChange={e => setPromoteKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                            className="bg-gray-800 border border-gray-600 text-gray-200 text-xs px-2.5 py-1.5 rounded-md w-48 font-mono focus:outline-none focus:border-brand-gold"
                            placeholder="my_strategy_name"
                          />
                        </div>
                        <button
                          onClick={handlePromote}
                          disabled={promoting || !promoteKey}
                          className="px-4 py-1.5 rounded-md text-xs font-semibold bg-green-700 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                        >
                          {promoting ? 'Promoting…' : '⭐ Promote to Built-in'}
                        </button>
                      </div>
                      {promoteError && (
                        <p className="text-red-400 text-xs mt-2">{promoteError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* ── Code panel ── */
            <>
              <button
                onClick={handleCopy}
                className="absolute top-3 right-4 z-10 px-2.5 py-1 text-[10px] rounded-md bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors border border-gray-700"
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
              <pre className="h-full overflow-auto p-5 text-xs font-mono text-gray-200 leading-relaxed scrollbar-thin">
                <code>{activeCode || '# (no source available)'}</code>
              </pre>
            </>
          )}
        </div>

        {/* Footer */}
        {data && !loading && (
          <div className="px-5 py-2.5 border-t border-gray-800 shrink-0 flex items-center justify-between gap-4">
            <span className="text-[10px] text-gray-600">
              {tab !== 'validation' && activeCode ? `${activeCode.split('\n').length} lines · ` : ''}
              {tab === 'logic' && 'Layer 1 — StrategyLogic (pandas)'}
              {tab === 'vectorized' && 'Layer 3 — pure NumPy (vectorbt)'}
              {tab === 'validation' && (validateResult ? (validateResult.all_passed ? 'All checks pass' : 'Validation failed') : '')}
            </span>

            {/* Validate + Promote buttons */}
            {canPromote && tab !== 'validation' && (
              <button
                onClick={handleValidate}
                disabled={validating}
                className="px-3 py-1.5 rounded-md text-[11px] font-medium bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors border border-gray-600 disabled:opacity-50"
              >
                {validating ? 'Validating…' : '✓ Validate'}
              </button>
            )}
            {canPromote && tab === 'validation' && validateResult?.all_passed && !promoted && (
              <button
                onClick={handleValidate}
                disabled={validating}
                className="px-3 py-1.5 rounded-md text-[11px] font-medium bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors border border-gray-600 disabled:opacity-50"
              >
                {validating ? 'Re-validating…' : '↺ Re-validate'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StrategiesPage = () => {
  const { user } = useAuth();
  const isSuperuser = user?.is_admin;

  const [strategies, setStrategies] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', strategy_class: 'ema_crossover', symbols: '', parameters: '{}', description: '' });
  const [creating, setCreating] = useState(false);
  const [backtestCache, setBacktestCache] = useState({});
  const [signalsCache, setSignalsCache] = useState({});   // strategyId → [signal]
  // Inner tab: 'single' | 'portfolio'  — Portfolio Deployments is the default landing view.
  const [innerTab, setInnerTab] = useState('portfolio');
  // Portfolios (for Portfolio inner tab)
  const [portfolios, setPortfolios] = useState([]);
  const [portfoliosLoading, setPortfoliosLoading] = useState(false);
  const [expandedPortfolio, setExpandedPortfolio] = useState(null); // portfolio id or null
  // Strategy detail modal — opened by clicking a portfolio member row.
  const [detailStrategyId, setDetailStrategyId] = useState(null);   // strategy id or null
  const [detailPortfolioId, setDetailPortfolioId] = useState(null); // portfolio context for track-record analytics
  // Code viewer
  const [codeViewKey, setCodeViewKey] = useState(null);   // strategy key being viewed
  // Grouping & filtering
  const [accountFilter, setAccountFilter] = useState(() => {
    // Default to whichever account is currently loaded in the Portfolio tab.
    try {
      const v = localStorage.getItem('maghaweer.portfolio.selected_account_id');
      return v ? Number(v) : 'all';
    } catch (_) { return 'all'; }
  });  // 'all' | account id (number)
  const [accounts, setAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  // Live indicator panel
  const [indicatorsCache,    setIndicatorsCache]    = useState({}); // strategyId → data
  const [indicatorsLoading,  setIndicatorsLoading]  = useState({}); // strategyId → bool
  // Audit Excel export
  const [auditLoading, setAuditLoading] = useState({}); // strategyId → bool
  // Pending (staged) orders surfaced read-only per strategy (visibility only).
  const [pendingByStrategy, setPendingByStrategy] = useState({}); // strategyId → [{orderId, symbol, orderAction, orderQty, accountIsPaper, slice}]
  const navigate = useNavigate();

  const loadStrategies = useCallback(() => {
    tradingService.getStrategies()
      .then(res => {
        const strats = res.data.results || res.data || [];
        setStrategies(strats);
        strats.forEach(s => {
          tradingService.getBacktests(s.id)
            .then(r => {
              const bts = r.data || [];
              if (bts.length > 0) setBacktestCache(prev => ({ ...prev, [s.id]: bts[0] }));
            })
            .catch(() => {});
          // Load live signals for active strategies
          if (s.is_active) {
            tradingService.getLiveSignals(s.id)
              .then(r => setSignalsCache(prev => ({ ...prev, [s.id]: r.data || [] })))
              .catch(() => {});
          }
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);


  // Pending orders, surfaced INSIDE the Strategies tab so cancellation happens
  // where the decision lives. We fetch staged orders and fan their per-strategy
  // contribution slices out into a {strategyId → [slice...]} map. Each slice
  // carries its own intent kind + allowed actions from the serializer.
  const loadPendingOrders = useCallback(() => {
    tradingService.listOrders({ status: 'staged', limit: 200 })
      .then(res => {
        const orders = res.data?.results || res.data || [];
        const map = {};
        orders.forEach(o => {
          (o.contributing || []).forEach(c => {
            if (!c.can_slice_cancel) return;
            (map[c.strategy_id] = map[c.strategy_id] || []).push({
              orderId: o.id,
              symbol: o.symbol,
              orderAction: o.action,
              orderQty: o.quantity,
              accountIsPaper: o.account_is_paper,
              slice: c,
            });
          });
        });
        setPendingByStrategy(map);
      })
      .catch(() => {});
  }, []);

  const loadPortfolios = useCallback(() => {
    setPortfoliosLoading(true);
    tradingService.listPortfolios()
      .then(res => setPortfolios(res.data?.results || res.data || []))
      .catch(() => {})
      .finally(() => setPortfoliosLoading(false));
  }, []);

  useEffect(() => {
    loadStrategies();
    loadPortfolios();
    loadPendingOrders();
    tradingService.getAvailableStrategies()
      .then(res => setAvailableTypes(res.data || []))
      .catch(() => {});
    tradingService.listIBKRAccounts()
      .then(res => setAccounts(Array.isArray(res.data) ? res.data : (res.data?.results || [])))
      .catch(() => {});
  }, [loadStrategies, loadPortfolios, loadPendingOrders]);

  // If the portfolio-default account no longer exists for this user, fall back to All.
  useEffect(() => {
    if (accountFilter !== 'all' && accounts.length > 0 && !accounts.some(a => a.id === accountFilter)) {
      setAccountFilter('all');
    }
  }, [accounts, accountFilter]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const symbolsList = form.symbols.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
      let params = {};
      try { params = JSON.parse(form.parameters); } catch (_) {}
      await tradingService.createStrategy({
        name: form.name,
        strategy_class: form.strategy_class,
        symbols: symbolsList,
        parameters: params,
        description: form.description,
      });
      setShowCreate(false);
      setForm({ name: '', strategy_class: 'ema_crossover', symbols: '', parameters: '{}', description: '' });
      loadStrategies();
    } catch (e) {
      alert('Failed: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (strategy) => {
    try {
      const res = await tradingService.updateStrategy(strategy.id, { is_active: !strategy.is_active });
      // A model account with its order sheet out: the stop is recorded and applied at fills / No fills (#40)
      if (res?.data?.stop_pending) window.alert(res.data.message);
      loadStrategies();
    } catch (_) {}
  };

  const handleDelete = async (strategy) => {
    if (strategy.is_active) {
      alert(`"${strategy.name}" is currently active.\n\nDeactivate it first to ensure any open IBKR positions are properly managed before deleting.`);
      return;
    }
    if (!window.confirm(`Delete strategy "${strategy.name}"?\n\nThis will permanently remove all signals and backtest records for this strategy.`)) return;
    try {
      await tradingService.deleteStrategy(strategy.id);
      loadStrategies();
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      alert(`Delete failed: ${msg}`);
    }
  };

  // Wishlist #40: withdraw a stop that is waiting for the model account's order sheet to be resolved.
  const handleCancelStop = async (strategy) => {
    try {
      await tradingService.cancelStrategyStop(strategy.id);
      loadStrategies();
    } catch (e) {
      alert(`Cancel failed: ${e.response?.data?.error || e.message}`);
    }
  };

  // Withdraw = deactivate + liquidate. toggle_active flips is_active=false and
  // aggregate_account_desired then treats the (now inactive) strategy's holdings
  // as desired=cash → SELL deltas that liquidate, staged OPG for the next open.
  const handleWithdraw = async (strategy) => {
    if (!window.confirm(
      `Withdraw "${strategy.name}"?\n\n` +
      `This SELLS all of its holdings (queued for the next market open) and sets ` +
      `the strategy inactive. You can re-activate it later.`
    )) return;
    try {
      const res = await tradingService.toggleStrategyActive(strategy.id);
      if (res?.data?.stop_pending) window.alert(res.data.message);
      loadStrategies();
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      alert(`Withdraw failed: ${msg}`);
    }
  };

  // Capital change (wishlist #27). RUNNING strategy: the draft is an AMOUNT (+X / −X) — previewed, confirmed, then
  // applied: +X is a capital credit (no trade now); −X comes from spare cash first and only the shortfall is sold at
  // the next open (a relative-momentum member: the basket's capital falls, its next rebalance trims). STOPPED strategy:
  // the draft is the seed for its next deployment (plain PATCH, no flow, no trade).
  const fmtUsd = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  const describeCapitalPlan = (strategy, p) => {
    const st = p.state || {};
    const head = `${strategy.name}\nCapital ${fmtUsd(st.capital)} · value ${fmtUsd(st.equity)} · spare cash ${fmtUsd(st.free_cash)}\n\n`;
    if (p.kind === 'credit') return head + `Add ${fmtUsd(p.credit)} of capital. No trade now — used at the next entry / rebalance.`;
    if (p.kind === 'cash_debit') return head + `Remove ${fmtUsd(p.debit)} from spare cash. No trade.`;
    if (p.kind === 'cascade_debit') return head + `Remove ${fmtUsd(p.debit)} from the basket's capital. Its next rebalance trims the positions.`;
    if (p.kind === 'trim') return head + `Remove ${fmtUsd(p.debit)}: ${fmtUsd(p.from_cash)} from spare cash, and SELL ${p.sell_qty} ${p.symbol} (~${fmtUsd(p.sell_usd)}) at the next open.`;
    return head + JSON.stringify(p);
  };
  const handleSaveAlloc = async (strategy, draftValue) => {
    const val = parseFloat(String(draftValue).replace(/[$,\s]/g, ''));
    if (isNaN(val) || val === 0) { return; }
    try {
      if (strategy.is_active) {
        const pv = await tradingService.changeCapital(strategy.id, { delta: val, preview: true });
        if (!window.confirm(describeCapitalPlan(strategy, pv.data || {}) + '\n\nApply?')) return;
        const r = await tradingService.changeCapital(strategy.id, { delta: val });
        const d = r.data || {};
        alert(`Done — dated ${d.session}.` + (d.kind === 'trim' ? ` The SELL of ${d.sell_qty} ${d.symbol} is staged for the next open.` : ''));
        loadStrategies();
      } else {
        if (val <= 0) { return; }
        await tradingService.updateStrategy(strategy.id, { allocation_usd: val });
        setStrategies(prev => prev.map(s => s.id === strategy.id ? { ...s, allocation_usd: val } : s));
      }
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      alert(`Could not change the capital: ${msg}`);
    }
  };

  const goToBacktest = (strategy) => {
    navigate(`/trading/backtest?strategy=${strategy.strategy_class}&symbol=${(strategy.symbols || [])[0] || ''}&strategy_id=${strategy.id}`);
  };

  const getParamsHint = (cls) => {
    const found = availableTypes.find(a => a.name === cls);
    if (found?.parameters) return JSON.stringify(found.parameters);
    return '{}';
  };

  // Filtered + grouped strategies
  const filteredStrategies = useMemo(() => {
    let list = strategies;
    if (accountFilter !== 'all') {
      list = list.filter(s => s.account === accountFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q) || s.strategy_class.toLowerCase().includes(q));
    }
    if (symbolFilter.trim()) {
      const sym = symbolFilter.toUpperCase();
      list = list.filter(s => (s.symbols || []).some(sy => sy.includes(sym)));
    }
    return list;
  }, [strategies, accountFilter, searchQuery, symbolFilter]);

  // Portfolio Deployments use the SAME three filters as Single Strategies, but
  // keyed on the portfolio's members (a portfolio matches if ANY member matches).
  const filteredPortfolios = useMemo(() => {
    let list = portfolios;
    if (accountFilter !== 'all') {
      list = list.filter(p => (p.members || []).some(m => m.account === accountFilter));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => (p.name || '').toLowerCase().includes(q));
    }
    if (symbolFilter.trim()) {
      const sym = symbolFilter.toUpperCase();
      list = list.filter(p =>
        (p.members || []).some(m => (m.symbols || []).some(sy => (sy || '').toUpperCase().includes(sym)))
      );
    }
    return list;
  }, [portfolios, accountFilter, searchQuery, symbolFilter]);

  // Nested hierarchy: account → strategy class → stock (symbol) → strategy cards
  const nestedGroups = useMemo(() => {
    const classLabel = (cls) =>
      (availableTypes.find(t => t.name === cls)?.display_name || cls || 'unknown')
        .replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const acctMap = {};
    filteredStrategies.forEach(s => {
      const aKey = s.account != null ? `acct_${s.account}` : 'acct_none';
      if (!acctMap[aKey]) {
        acctMap[aKey] = {
          key: aKey,
          label: s.account_name || 'Unassigned',
          tag: s.account != null ? (s.account_is_paper ? 'Paper' : 'LIVE') : null,
          classMap: {},
        };
      }
      const acct = acctMap[aKey];

      const cKey = `${aKey}/${s.strategy_class || 'unknown'}`;
      if (!acct.classMap[cKey]) {
        acct.classMap[cKey] = {
          key: cKey,
          label: classLabel(s.strategy_class),
          classKey: s.strategy_class || 'unknown',
          symbolMap: {},
        };
      }
      const cls = acct.classMap[cKey];

      const sym = (s.symbols || [])[0] || '—';
      const syKey = `${cKey}/${sym}`;
      if (!cls.symbolMap[syKey]) cls.symbolMap[syKey] = { key: syKey, symbol: sym, strategies: [] };
      cls.symbolMap[syKey].strategies.push(s);
    });

    const byActiveThenLabel = (a, b) => {
      const aa = a.activeCount > 0 ? 0 : 1;
      const bb = b.activeCount > 0 ? 0 : 1;
      if (aa !== bb) return aa - bb;
      return String(a.label || a.symbol).localeCompare(String(b.label || b.symbol));
    };

    return Object.values(acctMap)
      .map(acct => {
        const classes = Object.values(acct.classMap)
          .map(cls => {
            const symbols = Object.values(cls.symbolMap)
              .map(sy => ({ ...sy, activeCount: sy.strategies.filter(s => s.is_active).length }))
              .sort(byActiveThenLabel);
            const stratCount = symbols.reduce((n, sy) => n + sy.strategies.length, 0);
            const activeCount = symbols.reduce((n, sy) => n + sy.activeCount, 0);
            return { ...cls, symbols, stratCount, activeCount };
          })
          .sort(byActiveThenLabel);
        const stratCount = classes.reduce((n, c) => n + c.stratCount, 0);
        const activeCount = classes.reduce((n, c) => n + c.activeCount, 0);
        return { ...acct, classes, stratCount, activeCount };
      })
      .sort(byActiveThenLabel);
  }, [filteredStrategies, availableTypes]);

  const toggleGroup = (key) => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const handleDownloadAudit = async (strategy, oosStart = null) => {
    setAuditLoading(prev => ({ ...prev, [strategy.id]: true }));
    try {
      const params = oosStart ? { oos_start: oosStart } : {};
      const resp   = await tradingService.downloadAuditExcel(strategy.id, params);
      const url    = URL.createObjectURL(new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href     = url;
      a.download = `${(strategy.symbols || [''])[0]}_${strategy.name.slice(0, 35).replace(/\s+/g, '_')}_audit.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Audit export failed: ' + (e?.response?.data ? 'Server error — check logs' : e.message));
    } finally {
      setAuditLoading(prev => ({ ...prev, [strategy.id]: false }));
    }
  };

  const handleCheckIndicators = async (strategyId) => {
    setIndicatorsLoading(prev => ({ ...prev, [strategyId]: true }));
    try {
      const r = await tradingService.getLiveIndicators(strategyId);
      setIndicatorsCache(prev => ({ ...prev, [strategyId]: r.data }));
    } catch (e) {
      setIndicatorsCache(prev => ({ ...prev, [strategyId]: { error: e?.response?.data?.error || 'Failed to load indicators' } }));
    } finally {
      setIndicatorsLoading(prev => ({ ...prev, [strategyId]: false }));
    }
  };

  const cardCls = 'bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4';
  const inputCls = 'w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold';

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-brand-gold" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading strategies...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-serif font-semibold text-gray-800 dark:text-gray-200">DEPLOYED STRATEGIES</h2>
        {innerTab === 'single' && (
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-brand-gold text-white hover:bg-brand-gold-dark font-medium"
          >
            <MdAdd size={14} /> New Strategy
          </button>
        )}
      </div>

      {/* Inner tab bar */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        {[
          { key: 'single',    label: 'Single Strategies',     count: strategies.length },
          { key: 'portfolio', label: 'Portfolio Deployments',  count: portfolios.length },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setInnerTab(t.key)}
            className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors mr-1 ${
              innerTab === t.key
                ? 'border-brand-gold text-brand-gold'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {t.label}
            <span className="ml-1.5 text-[10px] opacity-60">({t.count})</span>
          </button>
        ))}
      </div>

      {innerTab === 'single' && <>
      {/* Filter bar */}
      {strategies.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search strategies…"
            className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30 w-40"
          />
          <input
            type="text"
            value={symbolFilter}
            onChange={e => setSymbolFilter(e.target.value.toUpperCase())}
            placeholder="Symbol…"
            className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30 w-24"
          />
          {accounts.length >= 2 && (
            <select
              value={accountFilter}
              onChange={e => setAccountFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30"
              title="Filter by IBKR account"
            >
              <option value="all">All accounts</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name} {a.is_paper ? '(Paper)' : '(LIVE)'}
                </option>
              ))}
            </select>
          )}
          <span className="text-[10px] text-gray-400 ml-1">
            {filteredStrategies.length} strateg{filteredStrategies.length === 1 ? 'y' : 'ies'}
            {filteredStrategies.filter(s => s.is_active).length > 0 && (
              <span className="ml-1 text-green-400">· {filteredStrategies.filter(s => s.is_active).length} active</span>
            )}
          </span>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className={cardCls}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">CREATE STRATEGY</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Name</label>
              <input className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="My EMA Strategy" />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Strategy Type</label>
              <select
                className={inputCls}
                value={form.strategy_class}
                onChange={e => {
                  const cls = e.target.value;
                  setForm({ ...form, strategy_class: cls, parameters: getParamsHint(cls) });
                }}
              >
                {availableTypes.length > 0 ? (
                  availableTypes.map(s => (
                    <option key={s.name} value={s.name}>{s.display_name || s.name}</option>
                  ))
                ) : (
                  <>
                    <option value="ema_crossover">EMA Crossover</option>
                    <option value="mean_reversion">Mean Reversion</option>
                    <option value="momentum">Momentum</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Symbols (comma-separated)</label>
              <input className={inputCls} value={form.symbols} onChange={e => setForm({ ...form, symbols: e.target.value })} placeholder="AAPL, MSFT" />
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Parameters (JSON)</label>
              <input className={inputCls} value={form.parameters} onChange={e => setForm({ ...form, parameters: e.target.value })} placeholder='{"fast_ema": 9, "slow_ema": 21}' />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Description (optional)</label>
              <input className={inputCls} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Short description..." />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating || !form.name} className="px-4 py-2 rounded-lg bg-brand-gold text-white text-sm font-medium disabled:opacity-40">
              {creating ? 'Creating...' : 'Create Strategy'}
            </button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 text-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Strategy list */}
      {strategies.length === 0 ? (
        <div className={cardCls}>
          <div className="text-center py-12">
            <MdTimeline size={32} className="mx-auto mb-3 text-gray-400" />
            <p className="text-sm text-gray-500">No strategies yet. Create one to get started.</p>
          </div>
        </div>
      ) : filteredStrategies.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">No strategies match your filters.</div>
      ) : (
        <div>
          {nestedGroups.map(acct => {
            const acctCollapsed = collapsedGroups[acct.key] ?? false;
            return (
              <div key={acct.key} className="mb-6">
                {/* ── Level 1: Account ── */}
                <button
                  onClick={() => toggleGroup(acct.key)}
                  className="w-full flex items-center justify-between mb-2 px-1 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800/30 rounded-lg transition-colors group"
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`text-[10px] transition-transform ${acctCollapsed ? '' : 'rotate-90'} text-gray-400 group-hover:text-brand-gold`}>▶</span>
                    <span className="text-sm font-serif font-semibold text-gray-800 dark:text-gray-200">{acct.label}</span>
                    {acct.tag && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                        acct.tag === 'LIVE'
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                      }`}>{acct.tag}</span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                      {acct.stratCount} strateg{acct.stratCount !== 1 ? 'ies' : 'y'}
                    </span>
                    {acct.activeCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                        {acct.activeCount} active
                      </span>
                    )}
                  </div>
                </button>

                {!acctCollapsed && (
                  <div className="pl-3 md:pl-4 border-l border-gray-100 dark:border-gray-800 ml-1">
                    {acct.classes.map(cls => {
                      const clsCollapsed = collapsedGroups[cls.key] ?? false;
                      return (
                        <div key={cls.key} className="mb-4">
                          {/* ── Level 2: Strategy class ── */}
                          <button
                            onClick={() => toggleGroup(cls.key)}
                            className="w-full flex items-center justify-between mb-2 px-1 py-1 hover:bg-gray-50 dark:hover:bg-gray-800/30 rounded-lg transition-colors group"
                          >
                            <div className="flex items-center gap-2.5">
                              <span className={`text-[10px] transition-transform ${clsCollapsed ? '' : 'rotate-90'} text-gray-400 group-hover:text-brand-gold`}>▶</span>
                              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{cls.label}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                                {cls.stratCount} variant{cls.stratCount !== 1 ? 's' : ''}
                              </span>
                              {cls.activeCount > 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                                  {cls.activeCount} active
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-[10px] text-gray-400">
                              <button
                                onClick={e => { e.stopPropagation(); setCodeViewKey(cls.classKey); }}
                                className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-brand-gold transition-colors"
                                title="View strategy source code"
                              >
                                <MdCode size={12} /> <span>Source</span>
                              </button>
                            </div>
                          </button>

                          {!clsCollapsed && (
                            <div className="pl-3 md:pl-4 border-l border-gray-100 dark:border-gray-800 ml-1">
                              {cls.symbols.map(sym => {
                                const symCollapsed = collapsedGroups[sym.key] ?? false;
                                const bestBt = sym.strategies
                                  .map(s => backtestCache[s.id])
                                  .filter(Boolean)
                                  .sort((a, b) => (b.sharpe_ratio || 0) - (a.sharpe_ratio || 0))[0];
                                return (
                                  <div key={sym.key} className="mb-4">
                                    {/* ── Level 3: Stock (symbol) ── */}
                                    <button
                                      onClick={() => toggleGroup(sym.key)}
                                      className="w-full flex items-center justify-between mb-2 px-1 py-1 hover:bg-gray-50 dark:hover:bg-gray-800/30 rounded-lg transition-colors group"
                                    >
                                      <div className="flex items-center gap-2.5">
                                        <span className={`text-[10px] transition-transform ${symCollapsed ? '' : 'rotate-90'} text-gray-400 group-hover:text-brand-gold`}>▶</span>
                                        <span className="text-xs font-mono font-semibold text-gray-700 dark:text-gray-300">{sym.symbol}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500">
                                          {sym.strategies.length}
                                        </span>
                                        {sym.activeCount > 0 && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                                            {sym.activeCount} active
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3 text-[10px] text-gray-400">
                                        {bestBt && (
                                          <>
                                            <span>Best Sharpe: <span className={bestBt.sharpe_ratio >= 0 ? 'text-green-400' : 'text-red-400'}>{bestBt.sharpe_ratio?.toFixed(2) ?? '—'}</span></span>
                                            <span>Best Return: <span className={bestBt.total_return >= 0 ? 'text-green-400' : 'text-red-400'}>{bestBt.total_return != null ? `${(bestBt.total_return * 100).toFixed(1)}%` : '—'}</span></span>
                                          </>
                                        )}
                                      </div>
                                    </button>

                                    {!symCollapsed && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {sym.strategies.map(s => (
                                          <StrategyCard
                                            key={s.id}
                                            s={s}
                                            bt={backtestCache[s.id]}
                                            sigs={signalsCache[s.id] || []}
                                            indicators={indicatorsCache[s.id]}
                                            indicatorsLoading={!!indicatorsLoading[s.id]}
                                            auditLoading={!!auditLoading[s.id]}
                                            pendingSlices={pendingByStrategy[s.id] || []}
                                            cardCls={cardCls}
                                            onToggleActive={() => toggleActive(s)}
                                            onDelete={() => handleDelete(s)}
                                            onWithdraw={() => handleWithdraw(s)}
                                            onCancelStop={() => handleCancelStop(s)}
                                            onSaveAlloc={(val) => handleSaveAlloc(s, val)}
                                            onGoBacktest={() => goToBacktest(s)}
                                            onDownloadAudit={() => handleDownloadAudit(s)}
                                            onCheckIndicators={() => handleCheckIndicators(s.id)}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      </> /* end innerTab === 'single' */}

      {/* ── Portfolio Deployments tab ── */}
      {innerTab === 'portfolio' && (
        <div>
          {/* Filter bar — same controls as Single Strategies, scoped to portfolios */}
          {portfolios.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search portfolios…"
                className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30 w-40"
              />
              <input
                type="text"
                value={symbolFilter}
                onChange={e => setSymbolFilter(e.target.value.toUpperCase())}
                placeholder="Symbol…"
                className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30 w-24"
              />
              {accounts.length >= 2 && (
                <select
                  value={accountFilter}
                  onChange={e => setAccountFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className="bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30"
                  title="Filter by IBKR account"
                >
                  <option value="all">All accounts</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name} {a.is_paper ? '(Paper)' : '(LIVE)'}
                    </option>
                  ))}
                </select>
              )}
              <span className="text-[10px] text-gray-400 ml-1">
                {filteredPortfolios.length} portfolio{filteredPortfolios.length === 1 ? '' : 's'}
                {filteredPortfolios.filter(p => p.is_active).length > 0 && (
                  <span className="ml-1 text-green-400">· {filteredPortfolios.filter(p => p.is_active).length} active</span>
                )}
              </span>
            </div>
          )}
          {portfoliosLoading ? (
            <div className="text-center py-12 text-gray-400 text-sm">Loading portfolios…</div>
          ) : portfolios.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
              <p className="font-medium mb-1">No portfolio deployments yet.</p>
              <p className="text-xs text-gray-500">Build a basket in the Portfolio Builder tab, then deploy it here.</p>
            </div>
          ) : filteredPortfolios.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No portfolios match your filters.</div>
          ) : (
            <div className="space-y-3">
              {filteredPortfolios.map(p => {
                const activeCount = p.active_count ?? 0;
                const memberCount = p.member_count ?? 0;
                return (
                  <div key={p.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-gray-800 dark:text-gray-100">{p.name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            p.is_active
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                          }`}>{p.is_active ? 'Active' : 'Inactive'}</span>
                          {p.optimizer_method && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                              {p.optimizer_method}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                          <span>{memberCount} strateg{memberCount !== 1 ? 'ies' : 'y'}</span>
                          <span>{activeCount} active</span>
                          {p.total_capital && <span>${Number(p.total_capital).toLocaleString()} capital</span>}
                        </div>
                        {/* Member chips */}
                        {p.members && p.members.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {p.members.slice(0, 6).map(m => (
                              <span key={m.id} className={`text-[9px] px-1.5 py-0.5 rounded border font-mono ${
                                m.is_active
                                  ? 'border-green-400/30 text-green-500 dark:text-green-400 bg-green-50 dark:bg-green-900/10'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-500'
                              }`}>
                                {m.strategy_name || m.strategy_class}
                                {m.symbols?.[0] && ` · ${m.symbols[0]}`}
                              </span>
                            ))}
                            {p.members.length > 6 && (
                              <span className="text-[9px] text-gray-400 px-1.5 py-0.5">+{p.members.length - 6} more</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={async () => {
                            const res = await tradingService.togglePortfolioActive(p.id);
                            if (res?.data?.signals_held_until) window.alert('Orders for this basket will be staged at 09:31 ET, after the live account\u2019s pre-open release, and go out at the next open.');
                            if (res?.data?.stops_pending?.length) window.alert(`${res.data.stops_pending.length} member(s) keep running for now: their account\u2019s order sheet is out with the operator. Their stops are applied when that sheet is resolved (fills uploaded or No fills).`);
                            loadPortfolios();
                          }}
                          className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                            p.is_active
                              ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40'
                              : 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/40'
                          }`}
                        >
                          {p.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => setExpandedPortfolio(prev => prev === p.id ? null : p.id)}
                          className="px-3 py-1.5 text-xs rounded-lg font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
                        >
                          Manage
                          <MdExpandMore
                            size={14}
                            className={`transition-transform ${expandedPortfolio === p.id ? 'rotate-180' : ''}`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Inline detail — loads in-page instead of navigating to analytics */}
                    {expandedPortfolio === p.id && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            Members
                          </h4>
                          <button
                            onClick={() => navigate('/trading/analytics/portfolio-builder')}
                            className="text-[10px] text-brand-gold hover:underline"
                          >
                            Open in Portfolio Builder →
                          </button>
                        </div>
                        {(!p.members || p.members.length === 0) ? (
                          <p className="text-xs text-gray-500 py-2">No members in this portfolio.</p>
                        ) : (
                          <div className="overflow-x-auto scrollbar-thin">
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800">
                                  <th className="py-1.5 pr-3 font-medium">Strategy</th>
                                  <th className="py-1.5 pr-3 font-medium">Symbol</th>
                                  <th className="py-1.5 pr-3 font-medium">Account</th>
                                  <th className="py-1.5 pr-3 font-medium text-right">Weight</th>
                                  <th className="py-1.5 pr-3 font-medium text-right">Allocation</th>
                                  <th className="py-1.5 pr-3 font-medium">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.members.map(m => (
                                  <tr
                                    key={m.id}
                                    onClick={() => { if (m.strategy_id != null) { setDetailStrategyId(m.strategy_id); setDetailPortfolioId(p.id); } }}
                                    className={`border-b border-gray-50 dark:border-gray-800/50 ${
                                      m.strategy_id != null
                                        ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors'
                                        : ''
                                    }`}
                                    title={m.strategy_id != null ? 'View strategy detail' : undefined}
                                  >
                                    <td className="py-1.5 pr-3 text-brand-gold font-medium">
                                      {m.strategy_name || m.strategy_class}
                                    </td>
                                    <td className="py-1.5 pr-3 font-mono text-gray-600 dark:text-gray-400">
                                      {(m.symbols || []).join(', ') || '—'}
                                    </td>
                                    <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-400">
                                      {m.account_name
                                        ? `${m.account_name} ${m.account_is_paper ? '(Paper)' : '(LIVE)'}`
                                        : '—'}
                                    </td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                                      {m.weight != null ? `${(Number(m.weight) * 100).toFixed(1)}%` : '—'}
                                    </td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                                      {m.allocation_usd != null ? `$${Number(m.allocation_usd).toLocaleString()}` : '—'}
                                    </td>
                                    <td className="py-1.5 pr-3">
                                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                        m.is_active
                                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                                      }`}>{m.is_active ? 'Active' : 'Inactive'}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Model portfolio: net percentage weights per security */}
                        <ModelPortfolioPanel portfolioId={p.id} />

                        {/* Backtest track-record analytics (regenerated curves) */}
                        <PortfolioAnalyticsPanel portfolioId={p.id} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Code Viewer Modal */}
      {codeViewKey && (
        <StrategyCodeModal
          strategyKey={codeViewKey}
          onClose={() => setCodeViewKey(null)}
          isSuperuser={isSuperuser}
        />
      )}

      {/* Strategy Detail Modal — opened from a portfolio member row. Reuses the
          same StrategyCard as the Single tab plus the deep backtest/optimization
          metrics panel. Mutations delegate to the existing handlers so list and
          modal stay consistent. */}
      {detailStrategyId != null && (
        <StrategyDetailModal
          strategyId={detailStrategyId}
          portfolioId={detailPortfolioId}
          pendingSlices={pendingByStrategy[detailStrategyId] || []}
          onClose={() => { setDetailStrategyId(null); setDetailPortfolioId(null); }}
          onToggleActive={(s) => toggleActive(s)}
          onDelete={(s) => handleDelete(s)}
          onWithdraw={(s) => handleWithdraw(s)}
          onCancelStop={(s) => handleCancelStop(s)}
          onSaveAlloc={(s, val) => handleSaveAlloc(s, val)}
          onMutated={() => { loadStrategies(); loadPortfolios(); loadPendingOrders(); }}
        />
      )}

      {/* NOTE: A per-strategy order slice-cancel dialog used to live here. It was
          removed in favour of strategy-level verbs (Withdraw / Change Alloc),
          which are the correct primitives under recompute-and-diff. The backend
          capability is retained dormant (POST orders/<pk>/cancel-slice/,
          apply_slice_cancel, tradingService.cancelOrderSlice) so the UI can be
          rebuilt from the same contract if we ever want manual order overrides. */}
    </div>
  );
};

export default StrategiesPage;
